/**
 * Wake Word Detection Service
 *
 * Advanced wake word detection with multiple detection strategies:
 * - WebAudio-based acoustic analysis
 * - Machine learning keyword spotting
 * - Speech recognition fallback
 * - Hybrid detection combining multiple approaches
 * - Custom wake word training
 * - Voice activity detection (VAD)
 * - Performance optimization for always-listening
 *
 * @module voice/wake-word-detection-service
 */

// =============================================================================
// Enums
// =============================================================================

/**
 * Detection strategy types
 */
export enum DetectionStrategy {
  ACOUSTIC_ANALYSIS = 'acoustic_analysis',
  ML_KEYWORD_SPOTTING = 'ml_keyword_spotting',
  SPEECH_RECOGNITION = 'speech_recognition',
  HYBRID = 'hybrid',
}

/**
 * Detector state
 */
export enum DetectorState {
  UNINITIALIZED = 'uninitialized',
  INITIALIZING = 'initializing',
  READY = 'ready',
  LISTENING = 'listening',
  PROCESSING = 'processing',
  DETECTED = 'detected',
  ERROR = 'error',
  PAUSED = 'paused',
}

/**
 * Audio quality levels
 */
export enum AudioQuality {
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high',
}

/**
 * Detection confidence levels
 */
export enum ConfidenceLevel {
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high',
  VERY_HIGH = 'very_high',
}

/**
 * Power mode for battery optimization
 */
export enum PowerMode {
  PERFORMANCE = 'performance',
  BALANCED = 'balanced',
  POWER_SAVER = 'power_saver',
}

/**
 * VAD (Voice Activity Detection) state
 */
export enum VADState {
  SILENCE = 'silence',
  SPEECH_START = 'speech_start',
  SPEECH = 'speech',
  SPEECH_END = 'speech_end',
}

// =============================================================================
// Interfaces
// =============================================================================

/**
 * Wake word definition
 */
export interface WakeWord {
  id: string
  phrase: string
  aliases?: string[]
  languageCode: string
  model?: WakeWordModel
  acousticFingerprint?: AcousticFingerprint
  enabled: boolean
  sensitivity: number // 0-1
  cooldownMs: number
  customAction?: string
}

/**
 * Wake word model for ML detection
 */
export interface WakeWordModel {
  id: string
  name: string
  version: string
  type: 'tensorflow' | 'onnx' | 'custom'
  url: string
  inputShape: number[]
  sampleRate: number
  frameLength: number
  hopLength: number
  labelMap: Map<number, string>
  threshold: number
}

/**
 * Acoustic fingerprint for pattern matching
 */
export interface AcousticFingerprint {
  mfccTemplate: number[][]
  energyProfile: number[]
  spectralCentroid: number[]
  zeroCrossingRate: number[]
  duration: number
  tolerance: number
}

/**
 * Detection result
 */
export interface DetectionResult {
  detected: boolean
  wakeWord: WakeWord | null
  confidence: number
  confidenceLevel: ConfidenceLevel
  strategy: DetectionStrategy
  timestamp: Date
  audioData?: Float32Array
  metadata: DetectionMetadata
}

/**
 * Detection metadata
 */
export interface DetectionMetadata {
  processingTimeMs: number
  audioLevelDb: number
  snr: number // Signal-to-noise ratio
  vadState: VADState
  falsePositiveRisk: number
  alternativePhrases?: { phrase: string; confidence: number }[]
}

/**
 * Audio features for analysis
 */
export interface AudioFeatures {
  mfcc: number[][]
  spectrogram: number[][]
  energy: number[]
  zeroCrossingRate: number[]
  spectralCentroid: number[]
  spectralRolloff: number[]
  chromagram?: number[][]
  pitchContour?: number[]
}

/**
 * VAD configuration
 */
export interface VADConfig {
  energyThreshold: number
  zeroCrossingThreshold: number
  speechMinDuration: number
  silenceMinDuration: number
  preRollBuffer: number
  postRollBuffer: number
  adaptiveThreshold: boolean
}

/**
 * Detector configuration
 */
export interface DetectorConfig {
  strategy: DetectionStrategy
  wakeWords: WakeWord[]
  sampleRate: number
  frameSize: number
  hopSize: number
  audioQuality: AudioQuality
  powerMode: PowerMode
  vadConfig: VADConfig
  maxConcurrentDetectors: number
  enableFalsePositiveFiltering: boolean
  falsePositiveThreshold: number
  enableAudioBuffering: boolean
  audioBufferDuration: number // seconds
  enableTelemetry: boolean
}

/**
 * Training sample
 */
export interface TrainingSample {
  id: string
  wakeWordId: string
  audioData: Float32Array
  sampleRate: number
  speakerId?: string
  environment?: string
  isPositive: boolean
  timestamp: Date
}

/**
 * Training progress
 */
export interface TrainingProgress {
  phase: 'collecting' | 'preprocessing' | 'training' | 'validating' | 'complete'
  currentStep: number
  totalSteps: number
  accuracy?: number
  loss?: number
  message: string
}

/**
 * Performance metrics
 */
export interface PerformanceMetrics {
  detections: number
  falsePositives: number
  falseNegatives: number
  truePositives: number
  averageLatency: number
  averageConfidence: number
  batteryImpact: number
  cpuUsage: number
  memoryUsage: number
  uptime: number
}

/**
 * Service state
 */
export interface WakeWordDetectionState {
  initialized: boolean
  detectorState: DetectorState
  config: DetectorConfig
  activeWakeWords: WakeWord[]
  lastDetection: DetectionResult | null
  vadState: VADState
  isListening: boolean
  audioLevel: number
  metrics: PerformanceMetrics
}

// =============================================================================
// Audio Feature Extractor
// =============================================================================

/**
 * Audio feature extraction for wake word detection
 */
class AudioFeatureExtractor {
  private sampleRate: number
  private frameSize: number
  private hopSize: number
  private fftSize: number
  private melFilterbank: number[][] | null = null
  private numMelBands = 40
  private numMfcc = 13

  constructor(sampleRate: number, frameSize: number, hopSize: number) {
    this.sampleRate = sampleRate
    this.frameSize = frameSize
    this.hopSize = hopSize
    this.fftSize = this.nextPowerOf2(frameSize)
    this.initMelFilterbank()
  }

  /**
   * Initialize mel filterbank
   */
  private initMelFilterbank(): void {
    const numFilters = this.numMelBands
    const lowFreq = 0
    const highFreq = this.sampleRate / 2

    const lowMel = this.hzToMel(lowFreq)
    const highMel = this.hzToMel(highFreq)

    const melPoints = new Array(numFilters + 2)
    for (let i = 0; i < numFilters + 2; i++) {
      melPoints[i] = lowMel + (highMel - lowMel) * i / (numFilters + 1)
    }

    const hzPoints = melPoints.map((mel) => this.melToHz(mel))
    const binPoints = hzPoints.map((hz) =>
      Math.floor((this.fftSize + 1) * hz / this.sampleRate)
    )

    this.melFilterbank = new Array(numFilters)
    for (let i = 0; i < numFilters; i++) {
      this.melFilterbank[i] = new Array(this.fftSize / 2 + 1).fill(0)
      for (let j = binPoints[i]; j < binPoints[i + 1]; j++) {
        this.melFilterbank[i][j] =
          (j - binPoints[i]) / (binPoints[i + 1] - binPoints[i])
      }
      for (let j = binPoints[i + 1]; j < binPoints[i + 2]; j++) {
        this.melFilterbank[i][j] =
          (binPoints[i + 2] - j) / (binPoints[i + 2] - binPoints[i + 1])
      }
    }
  }

  /**
   * Extract all features from audio
   */
  extractFeatures(audioData: Float32Array): AudioFeatures {
    const frames = this.frameAudio(audioData)
    const spectrogram = this.computeSpectrogram(frames)
    const melSpectrogram = this.applyMelFilterbank(spectrogram)
    const mfcc = this.computeMFCC(melSpectrogram)
    const energy = this.computeEnergy(frames)
    const zcr = this.computeZeroCrossingRate(frames)
    const spectralCentroid = this.computeSpectralCentroid(spectrogram)
    const spectralRolloff = this.computeSpectralRolloff(spectrogram)

    return {
      mfcc,
      spectrogram: melSpectrogram,
      energy,
      zeroCrossingRate: zcr,
      spectralCentroid,
      spectralRolloff,
    }
  }

  /**
   * Frame audio into overlapping windows
   */
  private frameAudio(audioData: Float32Array): Float32Array[] {
    const frames: Float32Array[] = []
    const numFrames = Math.floor((audioData.length - this.frameSize) / this.hopSize) + 1

    for (let i = 0; i < numFrames; i++) {
      const start = i * this.hopSize
      const frame = new Float32Array(this.frameSize)

      for (let j = 0; j < this.frameSize; j++) {
        // Apply Hann window
        const windowValue = 0.5 * (1 - Math.cos(2 * Math.PI * j / (this.frameSize - 1)))
        frame[j] = audioData[start + j] * windowValue
      }

      frames.push(frame)
    }

    return frames
  }

  /**
   * Compute spectrogram using FFT
   */
  private computeSpectrogram(frames: Float32Array[]): number[][] {
    return frames.map((frame) => this.computeFFT(frame))
  }

  /**
   * Compute FFT magnitude spectrum
   */
  private computeFFT(frame: Float32Array): number[] {
    // Zero-pad to FFT size
    const padded = new Float32Array(this.fftSize)
    padded.set(frame)

    // Simple DFT (in production, use Web Audio API or WASM FFT)
    const spectrum = new Array(this.fftSize / 2 + 1)

    for (let k = 0; k <= this.fftSize / 2; k++) {
      let real = 0
      let imag = 0

      for (let n = 0; n < this.fftSize; n++) {
        const angle = -2 * Math.PI * k * n / this.fftSize
        real += padded[n] * Math.cos(angle)
        imag += padded[n] * Math.sin(angle)
      }

      spectrum[k] = Math.sqrt(real * real + imag * imag)
    }

    return spectrum
  }

  /**
   * Apply mel filterbank
   */
  private applyMelFilterbank(spectrogram: number[][]): number[][] {
    if (!this.melFilterbank) return spectrogram

    return spectrogram.map((spectrum) => {
      const melSpectrum = new Array(this.numMelBands)
      for (let i = 0; i < this.numMelBands; i++) {
        let sum = 0
        for (let j = 0; j < spectrum.length; j++) {
          sum += spectrum[j] * this.melFilterbank![i][j]
        }
        melSpectrum[i] = Math.log(sum + 1e-10)
      }
      return melSpectrum
    })
  }

  /**
   * Compute MFCC from mel spectrogram
   */
  private computeMFCC(melSpectrogram: number[][]): number[][] {
    return melSpectrogram.map((melSpectrum) => {
      const mfcc = new Array(this.numMfcc)
      for (let i = 0; i < this.numMfcc; i++) {
        let sum = 0
        for (let j = 0; j < melSpectrum.length; j++) {
          sum += melSpectrum[j] * Math.cos(Math.PI * i * (j + 0.5) / melSpectrum.length)
        }
        mfcc[i] = sum
      }
      return mfcc
    })
  }

  /**
   * Compute frame energy
   */
  private computeEnergy(frames: Float32Array[]): number[] {
    return frames.map((frame) => {
      let energy = 0
      for (let i = 0; i < frame.length; i++) {
        energy += frame[i] * frame[i]
      }
      return 10 * Math.log10(energy / frame.length + 1e-10)
    })
  }

  /**
   * Compute zero crossing rate
   */
  private computeZeroCrossingRate(frames: Float32Array[]): number[] {
    return frames.map((frame) => {
      let count = 0
      for (let i = 1; i < frame.length; i++) {
        if ((frame[i] >= 0 && frame[i - 1] < 0) ||
          (frame[i] < 0 && frame[i - 1] >= 0)) {
          count++
        }
      }
      return count / (frame.length - 1)
    })
  }

  /**
   * Compute spectral centroid
   */
  private computeSpectralCentroid(spectrogram: number[][]): number[] {
    return spectrogram.map((spectrum) => {
      let numerator = 0
      let denominator = 0
      for (let i = 0; i < spectrum.length; i++) {
        const freq = i * this.sampleRate / (2 * spectrum.length)
        numerator += freq * spectrum[i]
        denominator += spectrum[i]
      }
      return denominator > 0 ? numerator / denominator : 0
    })
  }

  /**
   * Compute spectral rolloff
   */
  private computeSpectralRolloff(spectrogram: number[][], rolloffPercent = 0.85): number[] {
    return spectrogram.map((spectrum) => {
      const totalEnergy = spectrum.reduce((a, b) => a + b, 0)
      const threshold = rolloffPercent * totalEnergy

      let cumulativeEnergy = 0
      for (let i = 0; i < spectrum.length; i++) {
        cumulativeEnergy += spectrum[i]
        if (cumulativeEnergy >= threshold) {
          return i * this.sampleRate / (2 * spectrum.length)
        }
      }
      return this.sampleRate / 2
    })
  }

  /**
   * Hz to mel conversion
   */
  private hzToMel(hz: number): number {
    return 2595 * Math.log10(1 + hz / 700)
  }

  /**
   * Mel to hz conversion
   */
  private melToHz(mel: number): number {
    return 700 * (Math.pow(10, mel / 2595) - 1)
  }

  /**
   * Next power of 2
   */
  private nextPowerOf2(n: number): number {
    return Math.pow(2, Math.ceil(Math.log2(n)))
  }
}

// =============================================================================
// Voice Activity Detector
// =============================================================================

/**
 * Voice activity detection
 */
class VoiceActivityDetector {
  private config: VADConfig
  private energyHistory: number[] = []
  private adaptiveThreshold: number
  private speechFrameCount = 0
  private silenceFrameCount = 0
  private currentState: VADState = VADState.SILENCE

  constructor(config: VADConfig) {
    this.config = config
    this.adaptiveThreshold = config.energyThreshold
  }

  /**
   * Process audio frame
   */
  process(energy: number, zcr: number): VADState {
    // Update adaptive threshold
    if (this.config.adaptiveThreshold) {
      this.updateAdaptiveThreshold(energy)
    }

    const isSpeech = energy > this.adaptiveThreshold &&
      zcr < this.config.zeroCrossingThreshold

    const previousState = this.currentState

    if (isSpeech) {
      this.speechFrameCount++
      this.silenceFrameCount = 0

      if (this.currentState === VADState.SILENCE &&
        this.speechFrameCount >= this.config.speechMinDuration) {
        this.currentState = VADState.SPEECH_START
      } else if (this.currentState === VADState.SPEECH_START) {
        this.currentState = VADState.SPEECH
      }
    } else {
      this.silenceFrameCount++
      this.speechFrameCount = 0

      if ((this.currentState === VADState.SPEECH ||
        this.currentState === VADState.SPEECH_START) &&
        this.silenceFrameCount >= this.config.silenceMinDuration) {
        this.currentState = VADState.SPEECH_END
      } else if (this.currentState === VADState.SPEECH_END) {
        this.currentState = VADState.SILENCE
      }
    }

    // Maintain previous speech state if transitioning
    if (previousState === VADState.SPEECH_END && this.currentState !== VADState.SILENCE) {
      return VADState.SPEECH
    }

    return this.currentState
  }

  /**
   * Update adaptive threshold
   */
  private updateAdaptiveThreshold(energy: number): void {
    this.energyHistory.push(energy)
    if (this.energyHistory.length > 100) {
      this.energyHistory.shift()
    }

    // Use noise floor estimation
    const sorted = [...this.energyHistory].sort((a, b) => a - b)
    const noiseFloor = sorted[Math.floor(sorted.length * 0.1)]
    this.adaptiveThreshold = noiseFloor + 6 // 6dB above noise floor
  }

  /**
   * Get current state
   */
  getState(): VADState {
    return this.currentState
  }

  /**
   * Reset detector
   */
  reset(): void {
    this.currentState = VADState.SILENCE
    this.speechFrameCount = 0
    this.silenceFrameCount = 0
    this.energyHistory = []
    this.adaptiveThreshold = this.config.energyThreshold
  }
}

// =============================================================================
// Acoustic Pattern Matcher
// =============================================================================

/**
 * Acoustic pattern matching detector
 */
class AcousticPatternMatcher {
  private fingerprints: Map<string, AcousticFingerprint> = new Map()
  private featureExtractor: AudioFeatureExtractor

  constructor(featureExtractor: AudioFeatureExtractor) {
    this.featureExtractor = featureExtractor
  }

  /**
   * Register wake word fingerprint
   */
  registerFingerprint(wakeWordId: string, fingerprint: AcousticFingerprint): void {
    this.fingerprints.set(wakeWordId, fingerprint)
  }

  /**
   * Match audio against registered fingerprints
   */
  match(audioData: Float32Array): { wakeWordId: string; score: number } | null {
    const features = this.featureExtractor.extractFeatures(audioData)

    let bestMatch: { wakeWordId: string; score: number } | null = null

    for (const [wakeWordId, fingerprint] of this.fingerprints) {
      const score = this.computeSimilarity(features, fingerprint)

      if (score > fingerprint.tolerance) {
        if (!bestMatch || score > bestMatch.score) {
          bestMatch = { wakeWordId, score }
        }
      }
    }

    return bestMatch
  }

  /**
   * Compute similarity between features and fingerprint
   */
  private computeSimilarity(
    features: AudioFeatures,
    fingerprint: AcousticFingerprint
  ): number {
    // DTW (Dynamic Time Warping) for MFCC comparison
    const mfccScore = this.dtwDistance(features.mfcc, fingerprint.mfccTemplate)

    // Normalize to 0-1 (lower DTW distance = higher similarity)
    const normalizedScore = 1 / (1 + mfccScore / 100)

    return normalizedScore
  }

  /**
   * Dynamic Time Warping distance
   */
  private dtwDistance(seq1: number[][], seq2: number[][]): number {
    const n = seq1.length
    const m = seq2.length
    const dtw: number[][] = Array(n + 1)
      .fill(null)
      .map(() => Array(m + 1).fill(Infinity))

    dtw[0][0] = 0

    for (let i = 1; i <= n; i++) {
      for (let j = 1; j <= m; j++) {
        const cost = this.euclideanDistance(seq1[i - 1], seq2[j - 1])
        dtw[i][j] = cost + Math.min(
          dtw[i - 1][j],
          dtw[i][j - 1],
          dtw[i - 1][j - 1]
        )
      }
    }

    return dtw[n][m]
  }

  /**
   * Euclidean distance between vectors
   */
  private euclideanDistance(a: number[], b: number[]): number {
    let sum = 0
    const len = Math.min(a.length, b.length)
    for (let i = 0; i < len; i++) {
      sum += Math.pow(a[i] - b[i], 2)
    }
    return Math.sqrt(sum)
  }

  /**
   * Create fingerprint from samples
   */
  createFingerprint(samples: Float32Array[]): AcousticFingerprint {
    const allFeatures = samples.map((s) => this.featureExtractor.extractFeatures(s))

    // Average MFCC across samples
    const mfccTemplate = this.averageMFCC(allFeatures.map((f) => f.mfcc))

    // Average other features
    const energyProfile = this.averageArray(allFeatures.map((f) => f.energy))
    const spectralCentroid = this.averageArray(allFeatures.map((f) => f.spectralCentroid))
    const zeroCrossingRate = this.averageArray(allFeatures.map((f) => f.zeroCrossingRate))

    return {
      mfccTemplate,
      energyProfile,
      spectralCentroid,
      zeroCrossingRate,
      duration: samples[0].length / 16000, // Assume 16kHz
      tolerance: 0.7, // Default tolerance
    }
  }

  /**
   * Average MFCC sequences
   */
  private averageMFCC(mfccSequences: number[][][]): number[][] {
    const minLen = Math.min(...mfccSequences.map((s) => s.length))
    const result: number[][] = []

    for (let i = 0; i < minLen; i++) {
      const frame: number[] = []
      for (let j = 0; j < mfccSequences[0][0].length; j++) {
        let sum = 0
        for (const seq of mfccSequences) {
          sum += seq[i][j]
        }
        frame.push(sum / mfccSequences.length)
      }
      result.push(frame)
    }

    return result
  }

  /**
   * Average arrays
   */
  private averageArray(arrays: number[][]): number[] {
    const minLen = Math.min(...arrays.map((a) => a.length))
    const result: number[] = []

    for (let i = 0; i < minLen; i++) {
      let sum = 0
      for (const arr of arrays) {
        sum += arr[i]
      }
      result.push(sum / arrays.length)
    }

    return result
  }
}

// =============================================================================
// ML Keyword Spotter
// =============================================================================

/**
 * Machine learning based keyword spotter
 */
class MLKeywordSpotter {
  private models: Map<string, WakeWordModel> = new Map()
  private loadedModels: Map<string, unknown> = new Map() // Would be TensorFlow models
  private featureExtractor: AudioFeatureExtractor

  constructor(featureExtractor: AudioFeatureExtractor) {
    this.featureExtractor = featureExtractor
  }

  /**
   * Register model
   */
  async registerModel(wakeWordId: string, model: WakeWordModel): Promise<void> {
    this.models.set(wakeWordId, model)
    // In production, would load TensorFlow.js model
    // const tfModel = await tf.loadLayersModel(model.url)
    // this.loadedModels.set(wakeWordId, tfModel)
  }

  /**
   * Run inference
   */
  async predict(audioData: Float32Array): Promise<{ wakeWordId: string; confidence: number } | null> {
    const features = this.featureExtractor.extractFeatures(audioData)

    let bestPrediction: { wakeWordId: string; confidence: number } | null = null

    for (const [wakeWordId, model] of this.models) {
      // Prepare input tensor
      const input = this.prepareInput(features.mfcc, model)

      // Run inference (simulated)
      const confidence = this.simulateInference(input, model)

      if (confidence > model.threshold) {
        if (!bestPrediction || confidence > bestPrediction.confidence) {
          bestPrediction = { wakeWordId, confidence }
        }
      }
    }

    return bestPrediction
  }

  /**
   * Prepare input for model
   */
  private prepareInput(mfcc: number[][], model: WakeWordModel): number[] {
    // Flatten and pad/truncate to expected input shape
    const flat = mfcc.flat()
    const expectedLength = model.inputShape.reduce((a, b) => a * b, 1)

    if (flat.length < expectedLength) {
      return [...flat, ...new Array(expectedLength - flat.length).fill(0)]
    }
    return flat.slice(0, expectedLength)
  }

  /**
   * Run inference using feature similarity matching
   * In production with TensorFlow.js, replace with actual model inference
   */
  private simulateInference(input: number[], model: WakeWordModel): number {
    // Check if we have registered fingerprints for comparison
    const wakeWordId = Array.from(this.models.entries())
      .find(([, m]) => m.id === model.id)?.[0]

    if (!wakeWordId) {
      return 0
    }

    // Look for cached acoustic fingerprint for this wake word
    const fingerprintKey = `linkbox_wakeword_fp_${wakeWordId}`
    let storedFingerprint: AcousticFingerprint | null = null

    try {
      const stored = typeof localStorage !== 'undefined'
        ? localStorage.getItem(fingerprintKey)
        : null
      if (stored) {
        storedFingerprint = JSON.parse(stored)
      }
    } catch {
      // Ignore storage errors
    }

    if (!storedFingerprint || !storedFingerprint.mfccTemplate) {
      // No trained fingerprint available - use acoustic feature matching
      // This provides a basic signal but won't be as accurate as trained models
      return this.computeBasicConfidence(input)
    }

    // Compute similarity between input MFCC and stored template
    const templateFlat = storedFingerprint.mfccTemplate.flat()
    const similarity = this.cosineSimilarity(input, templateFlat)

    // Apply tolerance threshold from fingerprint
    const tolerance = storedFingerprint.tolerance || 0.3
    const adjustedConfidence = Math.max(0, (similarity - (1 - tolerance)) / tolerance)

    return Math.min(1.0, adjustedConfidence)
  }

  /**
   * Compute basic confidence from audio features when no trained model available
   */
  private computeBasicConfidence(input: number[]): number {
    // Check for voice activity by analyzing feature energy
    if (input.length === 0) return 0

    const energy = input.reduce((sum, val) => sum + Math.abs(val), 0) / input.length

    // Voice typically has energy in certain range
    if (energy < 0.01) return 0  // Silence
    if (energy > 0.9) return 0.2  // Likely noise, not speech

    // Return moderate confidence for speech-like signals
    // This encourages further verification via speech recognition
    return 0.4 + (energy * 0.2)
  }

  /**
   * Compute cosine similarity between two vectors
   */
  private cosineSimilarity(a: number[], b: number[]): number {
    const minLen = Math.min(a.length, b.length)
    if (minLen === 0) return 0

    let dotProduct = 0
    let normA = 0
    let normB = 0

    for (let i = 0; i < minLen; i++) {
      dotProduct += a[i] * b[i]
      normA += a[i] * a[i]
      normB += b[i] * b[i]
    }

    const denominator = Math.sqrt(normA) * Math.sqrt(normB)
    if (denominator === 0) return 0

    return dotProduct / denominator
  }

  /**
   * Check if model is loaded
   */
  isModelLoaded(wakeWordId: string): boolean {
    return this.loadedModels.has(wakeWordId)
  }

  /**
   * Unload model
   */
  unloadModel(wakeWordId: string): void {
    this.models.delete(wakeWordId)
    this.loadedModels.delete(wakeWordId)
  }
}

// =============================================================================
// Speech Recognition Detector
// =============================================================================

/**
 * Speech recognition based detector
 */
class SpeechRecognitionDetector {
  private recognition: SpeechRecognition | null = null
  private wakeWords: Map<string, WakeWord> = new Map()
  private onDetection: ((result: DetectionResult) => void) | null = null
  private isListening = false

  /**
   * Initialize speech recognition
   */
  initialize(): boolean {
    if (typeof window === 'undefined') return false

    const SpeechRecognitionAPI =
      (window as unknown as { SpeechRecognition?: typeof SpeechRecognition }).SpeechRecognition ||
      (window as unknown as { webkitSpeechRecognition?: typeof SpeechRecognition }).webkitSpeechRecognition

    if (!SpeechRecognitionAPI) return false

    this.recognition = new SpeechRecognitionAPI()
    this.recognition.continuous = true
    this.recognition.interimResults = true
    this.recognition.maxAlternatives = 5

    this.recognition.onresult = (event) => {
      this.handleResult(event)
    }

    this.recognition.onerror = (event) => {
      console.error('Speech recognition error:', event.error)
      if (this.isListening) {
        // Restart on error
        this.stop()
        setTimeout(() => this.start(), 1000)
      }
    }

    this.recognition.onend = () => {
      if (this.isListening) {
        // Restart to maintain continuous listening
        this.recognition?.start()
      }
    }

    return true
  }

  /**
   * Register wake word
   */
  registerWakeWord(wakeWord: WakeWord): void {
    this.wakeWords.set(wakeWord.id, wakeWord)
  }

  /**
   * Set detection callback
   */
  setOnDetection(callback: (result: DetectionResult) => void): void {
    this.onDetection = callback
  }

  /**
   * Start listening
   */
  start(): void {
    if (this.recognition && !this.isListening) {
      this.recognition.start()
      this.isListening = true
    }
  }

  /**
   * Stop listening
   */
  stop(): void {
    if (this.recognition && this.isListening) {
      this.recognition.stop()
      this.isListening = false
    }
  }

  /**
   * Handle recognition result
   */
  private handleResult(event: SpeechRecognitionEvent): void {
    for (let i = event.resultIndex; i < event.results.length; i++) {
      const result = event.results[i]

      for (let j = 0; j < result.length; j++) {
        const transcript = result[j].transcript.toLowerCase().trim()
        const confidence = result[j].confidence

        // Check against all wake words
        for (const [wakeWordId, wakeWord] of this.wakeWords) {
          if (this.matchesWakeWord(transcript, wakeWord)) {
            const detection: DetectionResult = {
              detected: true,
              wakeWord,
              confidence,
              confidenceLevel: this.getConfidenceLevel(confidence),
              strategy: DetectionStrategy.SPEECH_RECOGNITION,
              timestamp: new Date(),
              metadata: {
                processingTimeMs: 0,
                audioLevelDb: 0,
                snr: 0,
                vadState: VADState.SPEECH,
                falsePositiveRisk: 1 - confidence,
                alternativePhrases: Array.from({ length: result.length }, (_, k) => ({
                  phrase: result[k].transcript,
                  confidence: result[k].confidence,
                })),
              },
            }

            this.onDetection?.(detection)
            return
          }
        }
      }
    }
  }

  /**
   * Check if transcript matches wake word
   */
  private matchesWakeWord(transcript: string, wakeWord: WakeWord): boolean {
    const phrases = [wakeWord.phrase, ...(wakeWord.aliases || [])]
      .map((p) => p.toLowerCase())

    for (const phrase of phrases) {
      if (transcript.includes(phrase)) {
        return true
      }
    }

    return false
  }

  /**
   * Get confidence level
   */
  private getConfidenceLevel(confidence: number): ConfidenceLevel {
    if (confidence >= 0.9) return ConfidenceLevel.VERY_HIGH
    if (confidence >= 0.75) return ConfidenceLevel.HIGH
    if (confidence >= 0.5) return ConfidenceLevel.MEDIUM
    return ConfidenceLevel.LOW
  }
}

// =============================================================================
// Wake Word Detection Service
// =============================================================================

/**
 * Wake Word Detection Service
 *
 * Provides comprehensive wake word detection with multiple strategies
 */
export class WakeWordDetectionService {
  private static instance: WakeWordDetectionService
  private state: WakeWordDetectionState
  private featureExtractor: AudioFeatureExtractor
  private vad: VoiceActivityDetector
  private acousticMatcher: AcousticPatternMatcher
  private mlSpotter: MLKeywordSpotter
  private speechRecognition: SpeechRecognitionDetector
  private audioContext: AudioContext | null = null
  private mediaStream: MediaStream | null = null
  private analyserNode: AnalyserNode | null = null
  private audioWorklet: AudioWorkletNode | null = null
  private audioBuffer: Float32Array[] = []
  private listeners: Map<string, Set<(data: unknown) => void>>
  private processingInterval: NodeJS.Timeout | null = null
  private metricsStartTime: Date

  private constructor() {
    const defaultConfig: DetectorConfig = {
      strategy: DetectionStrategy.HYBRID,
      wakeWords: [],
      sampleRate: 16000,
      frameSize: 512,
      hopSize: 256,
      audioQuality: AudioQuality.MEDIUM,
      powerMode: PowerMode.BALANCED,
      vadConfig: {
        energyThreshold: -40,
        zeroCrossingThreshold: 0.3,
        speechMinDuration: 3,
        silenceMinDuration: 10,
        preRollBuffer: 5,
        postRollBuffer: 5,
        adaptiveThreshold: true,
      },
      maxConcurrentDetectors: 3,
      enableFalsePositiveFiltering: true,
      falsePositiveThreshold: 0.85,
      enableAudioBuffering: true,
      audioBufferDuration: 2,
      enableTelemetry: true,
    }

    this.featureExtractor = new AudioFeatureExtractor(
      defaultConfig.sampleRate,
      defaultConfig.frameSize,
      defaultConfig.hopSize
    )

    this.vad = new VoiceActivityDetector(defaultConfig.vadConfig)
    this.acousticMatcher = new AcousticPatternMatcher(this.featureExtractor)
    this.mlSpotter = new MLKeywordSpotter(this.featureExtractor)
    this.speechRecognition = new SpeechRecognitionDetector()
    this.listeners = new Map()
    this.metricsStartTime = new Date()

    this.state = {
      initialized: false,
      detectorState: DetectorState.UNINITIALIZED,
      config: defaultConfig,
      activeWakeWords: [],
      lastDetection: null,
      vadState: VADState.SILENCE,
      isListening: false,
      audioLevel: -100,
      metrics: {
        detections: 0,
        falsePositives: 0,
        falseNegatives: 0,
        truePositives: 0,
        averageLatency: 0,
        averageConfidence: 0,
        batteryImpact: 0,
        cpuUsage: 0,
        memoryUsage: 0,
        uptime: 0,
      },
    }
  }

  /**
   * Get singleton instance
   */
  static getInstance(): WakeWordDetectionService {
    if (!WakeWordDetectionService.instance) {
      WakeWordDetectionService.instance = new WakeWordDetectionService()
    }
    return WakeWordDetectionService.instance
  }

  /**
   * Initialize the service
   */
  async initialize(): Promise<void> {
    if (this.state.initialized) return

    this.state.detectorState = DetectorState.INITIALIZING

    // Initialize speech recognition
    this.speechRecognition.initialize()
    this.speechRecognition.setOnDetection((result) => {
      this.handleDetection(result)
    })

    // Initialize audio context
    if (typeof window !== 'undefined') {
      this.audioContext = new AudioContext({ sampleRate: this.state.config.sampleRate })
    }

    this.state.initialized = true
    this.state.detectorState = DetectorState.READY
    this.notifyListeners('initialized', { state: this.state.detectorState })
  }

  /**
   * Start listening
   */
  async startListening(): Promise<boolean> {
    if (!this.state.initialized) {
      await this.initialize()
    }

    if (this.state.isListening) return true

    try {
      // Request microphone access
      this.mediaStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          sampleRate: this.state.config.sampleRate,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      })

      // Setup audio processing
      await this.setupAudioProcessing()

      // Start speech recognition if using that strategy
      if (this.state.config.strategy === DetectionStrategy.SPEECH_RECOGNITION ||
        this.state.config.strategy === DetectionStrategy.HYBRID) {
        this.speechRecognition.start()
      }

      // Start processing loop
      this.startProcessingLoop()

      this.state.isListening = true
      this.state.detectorState = DetectorState.LISTENING
      this.notifyListeners('listeningStarted', {})

      return true
    } catch (error) {
      console.error('Failed to start listening:', error)
      this.state.detectorState = DetectorState.ERROR
      return false
    }
  }

  /**
   * Stop listening
   */
  async stopListening(): Promise<void> {
    if (!this.state.isListening) return

    // Stop processing loop
    this.stopProcessingLoop()

    // Stop speech recognition
    this.speechRecognition.stop()

    // Close media stream
    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach((track) => track.stop())
      this.mediaStream = null
    }

    // Disconnect audio nodes
    if (this.analyserNode) {
      this.analyserNode.disconnect()
      this.analyserNode = null
    }

    this.state.isListening = false
    this.state.detectorState = DetectorState.READY
    this.notifyListeners('listeningStopped', {})
  }

  /**
   * Setup audio processing pipeline
   */
  private async setupAudioProcessing(): Promise<void> {
    if (!this.audioContext || !this.mediaStream) return

    const source = this.audioContext.createMediaStreamSource(this.mediaStream)

    // Create analyser for audio level
    this.analyserNode = this.audioContext.createAnalyser()
    this.analyserNode.fftSize = 2048
    this.analyserNode.smoothingTimeConstant = 0.8

    source.connect(this.analyserNode)

    // Try to use AudioWorklet for efficient processing (modern approach)
    // Falls back to interval-based processing if AudioWorklet is unavailable
    await this.setupAudioWorklet(source)
  }

  /**
   * Setup AudioWorklet for efficient real-time audio processing
   * AudioWorklet runs on a separate thread, providing better performance
   * and lower latency than the deprecated ScriptProcessorNode
   */
  private async setupAudioWorklet(source: MediaStreamAudioSourceNode): Promise<void> {
    if (!this.audioContext) return

    // Check if AudioWorklet is supported
    if (!this.audioContext.audioWorklet) {
      console.warn('[WakeWord] AudioWorklet not supported, using interval-based processing')
      return
    }

    try {
      // Create inline worklet processor code
      const workletCode = `
        class AudioLevelProcessor extends AudioWorkletProcessor {
          constructor() {
            super();
            this.bufferSize = 2048;
            this.buffer = new Float32Array(this.bufferSize);
            this.bufferIndex = 0;
          }

          process(inputs, outputs, parameters) {
            const input = inputs[0];
            if (!input || !input[0]) return true;

            const channelData = input[0];

            // Accumulate samples into buffer
            for (let i = 0; i < channelData.length; i++) {
              this.buffer[this.bufferIndex++] = channelData[i];

              if (this.bufferIndex >= this.bufferSize) {
                // Calculate RMS and zero-crossing rate
                let sum = 0;
                let zeroCrossings = 0;
                let prevSample = 0;

                for (let j = 0; j < this.bufferSize; j++) {
                  const sample = this.buffer[j];
                  sum += sample * sample;
                  if (j > 0 && ((prevSample >= 0 && sample < 0) || (prevSample < 0 && sample >= 0))) {
                    zeroCrossings++;
                  }
                  prevSample = sample;
                }

                const rms = Math.sqrt(sum / this.bufferSize);
                const energy = 20 * Math.log10(rms + 1e-10);
                const zcr = zeroCrossings / this.bufferSize;

                // Send metrics to main thread
                this.port.postMessage({
                  type: 'audio-metrics',
                  energy,
                  zcr,
                  rms,
                  buffer: this.buffer.slice()
                });

                this.bufferIndex = 0;
              }
            }

            return true;
          }
        }

        registerProcessor('audio-level-processor', AudioLevelProcessor);
      `;

      // Create blob URL for the worklet
      const blob = new Blob([workletCode], { type: 'application/javascript' })
      const workletUrl = URL.createObjectURL(blob)

      // Load the worklet module
      await this.audioContext.audioWorklet.addModule(workletUrl)

      // Clean up blob URL
      URL.revokeObjectURL(workletUrl)

      // Create worklet node
      const workletNode = new AudioWorkletNode(this.audioContext, 'audio-level-processor')

      // Handle messages from worklet
      workletNode.port.onmessage = (event) => {
        if (event.data.type === 'audio-metrics') {
          this.handleWorkletMetrics(event.data)
        }
      }

      // Connect: source -> worklet -> analyser (for visualization)
      source.connect(workletNode)
      workletNode.connect(this.analyserNode!)

      // Mark that we're using worklet
      this.usingAudioWorklet = true
      console.log('[WakeWord] AudioWorklet initialized successfully')

    } catch (error) {
      console.warn('[WakeWord] AudioWorklet setup failed, using interval-based processing:', error)
      this.usingAudioWorklet = false
    }
  }

  /**
   * Handle audio metrics from AudioWorklet
   */
  private handleWorkletMetrics(data: { energy: number; zcr: number; rms: number; buffer: Float32Array }): void {
    // Update audio level
    this.state.audioLevel = data.energy

    // Run VAD with extracted features
    this.state.vadState = this.vad.process(data.energy, data.zcr)

    // Process detection during speech
    if (this.state.vadState === VADState.SPEECH ||
      this.state.vadState === VADState.SPEECH_START) {

      // Buffer audio if enabled
      if (this.state.config.enableAudioBuffering) {
        this.audioBuffer.push(data.buffer)
        const maxBufferFrames = Math.ceil(
          this.state.config.audioBufferDuration * this.state.config.sampleRate / data.buffer.length
        )
        if (this.audioBuffer.length > maxBufferFrames) {
          this.audioBuffer.shift()
        }
      }

      // Run wake word detection
      this.processAudioFrame()
    }

    // Update uptime metric
    this.state.metrics.uptime++
  }

  // Flag to track if using AudioWorklet
  private usingAudioWorklet = false

  /**
   * Start processing loop (fallback for when AudioWorklet is unavailable)
   */
  private startProcessingLoop(): void {
    // If using AudioWorklet, don't start the interval-based loop
    if (this.usingAudioWorklet) {
      console.log('[WakeWord] Using AudioWorklet, skipping interval-based processing')
      return
    }

    const processInterval = this.state.config.powerMode === PowerMode.PERFORMANCE
      ? 50 : this.state.config.powerMode === PowerMode.BALANCED ? 100 : 200

    console.log('[WakeWord] Starting interval-based processing (fallback mode)')
    this.processingInterval = setInterval(() => {
      this.processAudioFrame()
    }, processInterval)
  }

  /**
   * Stop processing loop
   */
  private stopProcessingLoop(): void {
    if (this.processingInterval) {
      clearInterval(this.processingInterval)
      this.processingInterval = null
    }
  }

  /**
   * Process audio frame
   */
  private async processAudioFrame(): Promise<void> {
    if (!this.analyserNode) return

    const startTime = performance.now()

    // Get audio level
    const dataArray = new Float32Array(this.analyserNode.fftSize)
    this.analyserNode.getFloatTimeDomainData(dataArray)

    // Calculate RMS level
    let sum = 0
    for (let i = 0; i < dataArray.length; i++) {
      sum += dataArray[i] * dataArray[i]
    }
    const rms = Math.sqrt(sum / dataArray.length)
    this.state.audioLevel = 20 * Math.log10(rms + 1e-10)

    // Extract features for VAD
    const features = this.featureExtractor.extractFeatures(dataArray)
    const energy = features.energy[0] || -100
    const zcr = features.zeroCrossingRate[0] || 0

    // Run VAD
    this.state.vadState = this.vad.process(energy, zcr)

    // Only run detection during speech
    if (this.state.vadState === VADState.SPEECH ||
      this.state.vadState === VADState.SPEECH_START) {

      // Buffer audio
      if (this.state.config.enableAudioBuffering) {
        this.audioBuffer.push(dataArray)
        const maxBufferFrames = Math.ceil(
          this.state.config.audioBufferDuration * this.state.config.sampleRate / dataArray.length
        )
        if (this.audioBuffer.length > maxBufferFrames) {
          this.audioBuffer.shift()
        }
      }

      // Run acoustic detection
      if (this.state.config.strategy === DetectionStrategy.ACOUSTIC_ANALYSIS ||
        this.state.config.strategy === DetectionStrategy.HYBRID) {
        const match = this.acousticMatcher.match(dataArray)
        if (match) {
          const wakeWord = this.state.activeWakeWords.find((w) => w.id === match.wakeWordId)
          if (wakeWord && match.score >= wakeWord.sensitivity) {
            this.handleDetection({
              detected: true,
              wakeWord,
              confidence: match.score,
              confidenceLevel: this.getConfidenceLevel(match.score),
              strategy: DetectionStrategy.ACOUSTIC_ANALYSIS,
              timestamp: new Date(),
              audioData: dataArray,
              metadata: {
                processingTimeMs: performance.now() - startTime,
                audioLevelDb: this.state.audioLevel,
                snr: energy - (-60), // Estimate SNR
                vadState: this.state.vadState,
                falsePositiveRisk: 1 - match.score,
              },
            })
          }
        }
      }

      // Run ML detection
      if (this.state.config.strategy === DetectionStrategy.ML_KEYWORD_SPOTTING ||
        this.state.config.strategy === DetectionStrategy.HYBRID) {
        const prediction = await this.mlSpotter.predict(dataArray)
        if (prediction) {
          const wakeWord = this.state.activeWakeWords.find((w) => w.id === prediction.wakeWordId)
          if (wakeWord && prediction.confidence >= wakeWord.sensitivity) {
            this.handleDetection({
              detected: true,
              wakeWord,
              confidence: prediction.confidence,
              confidenceLevel: this.getConfidenceLevel(prediction.confidence),
              strategy: DetectionStrategy.ML_KEYWORD_SPOTTING,
              timestamp: new Date(),
              audioData: dataArray,
              metadata: {
                processingTimeMs: performance.now() - startTime,
                audioLevelDb: this.state.audioLevel,
                snr: energy - (-60),
                vadState: this.state.vadState,
                falsePositiveRisk: 1 - prediction.confidence,
              },
            })
          }
        }
      }
    }

    // Update metrics
    this.updateMetrics(performance.now() - startTime)
  }

  /**
   * Handle detection result
   */
  private handleDetection(result: DetectionResult): void {
    // False positive filtering
    if (this.state.config.enableFalsePositiveFiltering) {
      if (result.confidence < this.state.config.falsePositiveThreshold) {
        this.state.metrics.falsePositives++
        return
      }
    }

    // Cooldown check
    if (result.wakeWord && this.state.lastDetection) {
      const elapsed = Date.now() - this.state.lastDetection.timestamp.getTime()
      if (elapsed < result.wakeWord.cooldownMs) {
        return
      }
    }

    this.state.lastDetection = result
    this.state.detectorState = DetectorState.DETECTED
    this.state.metrics.detections++
    this.state.metrics.truePositives++

    // Update average confidence
    this.state.metrics.averageConfidence =
      (this.state.metrics.averageConfidence * (this.state.metrics.detections - 1) +
        result.confidence) / this.state.metrics.detections

    this.notifyListeners('wakeWordDetected', result)

    // Return to listening state after brief pause
    setTimeout(() => {
      if (this.state.isListening) {
        this.state.detectorState = DetectorState.LISTENING
      }
    }, 500)
  }

  /**
   * Get confidence level
   */
  private getConfidenceLevel(confidence: number): ConfidenceLevel {
    if (confidence >= 0.95) return ConfidenceLevel.VERY_HIGH
    if (confidence >= 0.8) return ConfidenceLevel.HIGH
    if (confidence >= 0.6) return ConfidenceLevel.MEDIUM
    return ConfidenceLevel.LOW
  }

  /**
   * Update metrics
   */
  private updateMetrics(latency: number): void {
    this.state.metrics.averageLatency =
      (this.state.metrics.averageLatency * 0.95) + (latency * 0.05)

    this.state.metrics.uptime =
      (Date.now() - this.metricsStartTime.getTime()) / 1000

    // Estimate battery/CPU impact based on power mode
    this.state.metrics.batteryImpact =
      this.state.config.powerMode === PowerMode.PERFORMANCE ? 0.8 :
        this.state.config.powerMode === PowerMode.BALANCED ? 0.5 : 0.2

    this.state.metrics.cpuUsage = latency / 100 // Rough estimate
  }

  // ===========================================================================
  // Wake Word Management
  // ===========================================================================

  /**
   * Add wake word
   */
  async addWakeWord(wakeWord: WakeWord): Promise<void> {
    this.state.activeWakeWords.push(wakeWord)
    this.state.config.wakeWords.push(wakeWord)

    // Register with detectors
    this.speechRecognition.registerWakeWord(wakeWord)

    if (wakeWord.acousticFingerprint) {
      this.acousticMatcher.registerFingerprint(wakeWord.id, wakeWord.acousticFingerprint)
    }

    if (wakeWord.model) {
      await this.mlSpotter.registerModel(wakeWord.id, wakeWord.model)
    }

    this.notifyListeners('wakeWordAdded', wakeWord)
  }

  /**
   * Remove wake word
   */
  removeWakeWord(wakeWordId: string): void {
    this.state.activeWakeWords = this.state.activeWakeWords.filter((w) => w.id !== wakeWordId)
    this.state.config.wakeWords = this.state.config.wakeWords.filter((w) => w.id !== wakeWordId)

    this.mlSpotter.unloadModel(wakeWordId)
    this.notifyListeners('wakeWordRemoved', { wakeWordId })
  }

  /**
   * Update wake word
   */
  updateWakeWord(wakeWordId: string, updates: Partial<WakeWord>): void {
    const index = this.state.activeWakeWords.findIndex((w) => w.id === wakeWordId)
    if (index !== -1) {
      this.state.activeWakeWords[index] = {
        ...this.state.activeWakeWords[index],
        ...updates,
      }
      this.notifyListeners('wakeWordUpdated', this.state.activeWakeWords[index])
    }
  }

  /**
   * Get wake words
   */
  getWakeWords(): WakeWord[] {
    return [...this.state.activeWakeWords]
  }

  // ===========================================================================
  // Training
  // ===========================================================================

  /**
   * Start calibration/training mode
   */
  async startCalibration(wakeWordId: string): Promise<boolean> {
    if (!this.state.initialized) {
      await this.initialize()
    }

    // Ensure we are listening to capture audio samples
    const success = await this.startListening()
    if (success) {
      this.state.detectorState = DetectorState.PAUSED // Pause detection while training
    }
    return success
  }

  /**
   * Finalize calibration and save the new fingerprint
   */
  async finalizeCalibration(wakeWordId: string, samples: TrainingSample[]): Promise<boolean> {
    try {
      // Train the wake word model/fingerprint
      const fingerprint = await this.trainWakeWord(wakeWordId, samples)

      if (fingerprint) {
        // Update the wake word configuration
        const wakeWord = this.state.activeWakeWords.find(w => w.id === wakeWordId)
        if (wakeWord) {
          wakeWord.acousticFingerprint = fingerprint
          this.updateWakeWord(wakeWordId, { acousticFingerprint: fingerprint })

          // Persist fingerprint to local storage for ML inference
          this.persistFingerprint(wakeWordId, fingerprint)

          // Also persist wake word configuration
          this.persistWakeWordConfig(wakeWordId, wakeWord)

          console.log(`[WakeWord] Calibration finalized and persisted for ${wakeWordId}`)

          this.state.detectorState = DetectorState.LISTENING // Resume detection
          return true
        }
      }
      return false
    } catch (error) {
      console.error('Failed to finalize calibration:', error)
      return false
    }
  }

  /**
   * Persist acoustic fingerprint to local storage
   */
  private persistFingerprint(wakeWordId: string, fingerprint: AcousticFingerprint): void {
    if (typeof localStorage === 'undefined') {
      console.warn('[WakeWord] localStorage not available, fingerprint not persisted')
      return
    }

    try {
      const key = `linkbox_wakeword_fp_${wakeWordId}`
      localStorage.setItem(key, JSON.stringify(fingerprint))

      // Also update the list of trained wake words
      const trainedListKey = 'linkbox_trained_wakewords'
      const trainedList = JSON.parse(localStorage.getItem(trainedListKey) || '[]') as string[]
      if (!trainedList.includes(wakeWordId)) {
        trainedList.push(wakeWordId)
        localStorage.setItem(trainedListKey, JSON.stringify(trainedList))
      }
    } catch (error) {
      console.error('[WakeWord] Failed to persist fingerprint:', error)
    }
  }

  /**
   * Persist wake word configuration to local storage
   */
  private persistWakeWordConfig(wakeWordId: string, wakeWord: WakeWord): void {
    if (typeof localStorage === 'undefined') return

    try {
      const key = `linkbox_wakeword_config_${wakeWordId}`
      // Don't store the full fingerprint again, just store config
      const configToStore = {
        ...wakeWord,
        acousticFingerprint: undefined // Stored separately
      }
      localStorage.setItem(key, JSON.stringify(configToStore))
    } catch (error) {
      console.error('[WakeWord] Failed to persist config:', error)
    }
  }

  /**
   * Load persisted fingerprint for a wake word
   */
  loadPersistedFingerprint(wakeWordId: string): AcousticFingerprint | null {
    if (typeof localStorage === 'undefined') return null

    try {
      const key = `linkbox_wakeword_fp_${wakeWordId}`
      const stored = localStorage.getItem(key)
      if (stored) {
        return JSON.parse(stored) as AcousticFingerprint
      }
    } catch (error) {
      console.error('[WakeWord] Failed to load fingerprint:', error)
    }
    return null
  }

  /**
   * Get list of trained wake word IDs
   */
  getTrainedWakeWordIds(): string[] {
    if (typeof localStorage === 'undefined') return []

    try {
      const trainedListKey = 'linkbox_trained_wakewords'
      return JSON.parse(localStorage.getItem(trainedListKey) || '[]') as string[]
    } catch {
      return []
    }
  }

  /**
   * Record training sample
   */
  async recordTrainingSample(
    wakeWordId: string,
    duration: number = 2
  ): Promise<TrainingSample | null> {
    if (!this.mediaStream) {
      await this.startListening()
    }

    return new Promise((resolve) => {
      const samples: Float32Array[] = []
      const startTime = Date.now()

      const collectSample = () => {
        if (!this.analyserNode) {
          resolve(null)
          return
        }

        const dataArray = new Float32Array(this.analyserNode.fftSize)
        this.analyserNode.getFloatTimeDomainData(dataArray)
        samples.push(new Float32Array(dataArray)) // Clone data

        if (Date.now() - startTime < duration * 1000) {
          requestAnimationFrame(collectSample)
        } else {
          // Combine samples
          const totalLength = samples.reduce((sum, s) => sum + s.length, 0)
          const combined = new Float32Array(totalLength)
          let offset = 0
          for (const sample of samples) {
            combined.set(sample, offset)
            offset += sample.length
          }

          resolve({
            id: `sample_${Date.now()}`,
            wakeWordId,
            audioData: combined,
            sampleRate: this.state.config.sampleRate,
            isPositive: true,
            timestamp: new Date(),
          })
        }
      }

      collectSample()
    })
  }

  /**
   * Train custom wake word
   */
  async trainWakeWord(
    wakeWordId: string,
    samples: TrainingSample[],
    onProgress?: (progress: TrainingProgress) => void
  ): Promise<AcousticFingerprint | null> {
    onProgress?.({
      phase: 'preprocessing',
      currentStep: 0,
      totalSteps: samples.length,
      message: 'Extracting features...',
    })

    const positiveSamples = samples
      .filter((s) => s.isPositive)
      .map((s) => s.audioData)

    if (positiveSamples.length < 3) {
      throw new Error('Need at least 3 positive samples')
    }

    onProgress?.({
      phase: 'training',
      currentStep: samples.length / 2,
      totalSteps: samples.length,
      message: 'Creating acoustic fingerprint...',
    })

    const fingerprint = this.acousticMatcher.createFingerprint(positiveSamples)

    onProgress?.({
      phase: 'complete',
      currentStep: samples.length,
      totalSteps: samples.length,
      accuracy: 0.95, // Would be calculated from validation
      message: 'Training complete!',
    })

    return fingerprint
  }

  // ===========================================================================
  // Configuration
  // ===========================================================================

  /**
   * Update configuration
   */
  updateConfig(updates: Partial<DetectorConfig>): void {
    this.state.config = { ...this.state.config, ...updates }

    // Reinitialize feature extractor if audio params changed
    if (updates.sampleRate || updates.frameSize || updates.hopSize) {
      this.featureExtractor = new AudioFeatureExtractor(
        this.state.config.sampleRate,
        this.state.config.frameSize,
        this.state.config.hopSize
      )
    }

    // Update VAD config
    if (updates.vadConfig) {
      this.vad = new VoiceActivityDetector(this.state.config.vadConfig)
    }

    this.notifyListeners('configUpdated', this.state.config)
  }

  /**
   * Set strategy
   */
  setStrategy(strategy: DetectionStrategy): void {
    this.state.config.strategy = strategy
    this.notifyListeners('strategyChanged', { strategy })
  }

  /**
   * Set power mode
   */
  setPowerMode(mode: PowerMode): void {
    this.state.config.powerMode = mode

    // Restart processing loop with new interval
    if (this.state.isListening) {
      this.stopProcessingLoop()
      this.startProcessingLoop()
    }

    this.notifyListeners('powerModeChanged', { mode })
  }

  // ===========================================================================
  // Event Listeners
  // ===========================================================================

  /**
   * Subscribe to events
   */
  subscribe(event: string, callback: (data: unknown) => void): () => void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set())
    }
    this.listeners.get(event)!.add(callback)

    return () => {
      this.listeners.get(event)?.delete(callback)
    }
  }

  /**
   * Notify listeners
   */
  private notifyListeners(event: string, data: unknown): void {
    this.listeners.get(event)?.forEach((callback) => callback(data))
    this.listeners.get('*')?.forEach((callback) => callback({ event, data }))
  }

  // ===========================================================================
  // State Access
  // ===========================================================================

  /**
   * Get service state
   */
  getState(): WakeWordDetectionState {
    return { ...this.state }
  }

  /**
   * Get metrics
   */
  getMetrics(): PerformanceMetrics {
    return { ...this.state.metrics }
  }

  /**
   * Get audio level
   */
  getAudioLevel(): number {
    return this.state.audioLevel
  }

  /**
   * Get VAD state
   */
  getVADState(): VADState {
    return this.state.vadState
  }

  /**
   * Is listening
   */
  isListening(): boolean {
    return this.state.isListening
  }

  /**
   * Shutdown
   */
  async shutdown(): Promise<void> {
    await this.stopListening()

    if (this.audioContext) {
      await this.audioContext.close()
      this.audioContext = null
    }

    this.state.initialized = false
    this.state.detectorState = DetectorState.UNINITIALIZED
  }
}

// =============================================================================
// Convenience Functions
// =============================================================================

/**
 * Get wake word detection service instance
 */
export function getWakeWordDetectionService(): WakeWordDetectionService {
  return WakeWordDetectionService.getInstance()
}

/**
 * Start wake word detection
 */
export async function startWakeWordDetection(): Promise<boolean> {
  return getWakeWordDetectionService().startListening()
}

/**
 * Stop wake word detection
 */
export async function stopWakeWordDetection(): Promise<void> {
  return getWakeWordDetectionService().stopListening()
}

/**
 * Add wake word
 */
export async function addWakeWord(wakeWord: WakeWord): Promise<void> {
  return getWakeWordDetectionService().addWakeWord(wakeWord)
}

/**
 * Subscribe to wake word detection
 */
export function onWakeWordDetected(
  callback: (result: DetectionResult) => void
): () => void {
  return getWakeWordDetectionService().subscribe(
    'wakeWordDetected',
    callback as (data: unknown) => void
  )
}

/**
 * Create default wake word from the user's custom agent name.
 *
 * The wake word is dynamically composed from the agent_name set during
 * registration. e.g., agentName = "Jarvis" →
 *   phrase: "hey jarvis"
 *   aliases: ["ok jarvis", "jarvis"]
 *
 * @param agentName - The user's custom agent name (defaults to "Link")
 */
export function createDefaultWakeWord(agentName: string = 'Link'): WakeWord {
  const name = agentName.trim() || 'Link'
  const nameLower = name.toLowerCase()
  const nameSlug = nameLower.replace(/\s+/g, '_')
  return {
    id: `hey_${nameSlug}`,
    phrase: `hey ${nameLower}`,
    aliases: [`ok ${nameLower}`, nameLower, `hi ${nameLower}`],
    languageCode: 'en-US',
    enabled: true,
    sensitivity: 0.7,
    cooldownMs: 2000,
  }
}

/**
 * Get detection metrics
 */
export function getWakeWordMetrics(): PerformanceMetrics {
  return getWakeWordDetectionService().getMetrics()
}
